import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";

function formatSv(date) {
  if (!date) return "-";
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}-${yyyy} ${HH}:${MM}`;
}

export default function Admin() {
  const {
    user: me,
    adminCreateUser,
    adminFetchUsers,
    adminSetRole,
    adminDeleteUser,
  } = useAuth();

  // Skapa användare
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [createMsg, setCreateMsg] = useState("");

  // Lista/hantera
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const loadUsers = async () => {
    setLoading(true);
    try {
      const list = await adminFetchUsers();
      setUsers(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    setCreateMsg("");
    if (!email) return;
    try {
      const u = await adminCreateUser({ email, role });
      setCreateMsg(
        `Skapade ett konto till ${u.email}. Inbjudningsmail skickat.`
      );
      setEmail("");
      setRole("employee");
      loadUsers();
    } catch (e) {
      setCreateMsg(e?.response?.data?.message || "Kunde inte skapa användare");
    }
  };

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    if (u.role === "admin" && newRole !== "admin") {
      if (!window.confirm("Är du säker på att du vill degradera en admin?")) return;
    }
    try {
      await adminSetRole(u.id, newRole);
      loadUsers();
    } catch (e) {
      alert(e?.response?.data?.message || "Kunde inte uppdatera rollen");
    }
  };

  const removeUser = async (u) => {
    if (String(u.id) === String(me?.id)) {
      alert("Du kan inte ta bort dig själv.");
      return;
    }
    if (!window.confirm(`Ta bort ${u.email}?`)) return;
    try {
      await adminDeleteUser(u.id);
      loadUsers();
    } catch (e) {
      alert(e?.response?.data?.message || "Kunde inte ta bort användare");
    }
  };

  const filtered = useMemo(() => {
    if (!filter.trim()) return users;
    const q = filter.trim().toLowerCase();
    return users.filter((u) =>
      [u.email, u.firstName, u.lastName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, filter]);

  return (
    <div className="container mt-5" style={{ maxWidth: 980 }}>
      <h3 className="mb-4">Användarhanterare</h3>

      {/* Skapa ny användare */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Skapa användare</h5>
          {createMsg && <div className="alert alert-info">{createMsg}</div>}
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label">E-post</label>
              <input
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Roll</label>
              <select
                className="form-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="employee">Anställd</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={handleCreate}>
                Skapa
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="row mb-2">
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Filtrera på namn eller e-post…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Lista användare */}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">Alla användare</h5>

          {loading ? (
            <div className="text-muted">Laddar…</div>
          ) : filtered.length === 0 ? (
            <div className="text-muted">Inga användare matchar filtret</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}></th>
                    <th>Namn</th>
                    <th>E-post</th>
                    <th>Roll</th>
                    <th>Skapad</th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id}>
                      {/* Avatar */}
                      <td>
                        <Avatar
                          initials={u?.avatar?.initials}
                          color={u?.avatar?.color}
                          size={28}
                          title={`${u.firstName || ""} ${u.lastName || ""}`.trim()}
                        />
                      </td>

                      {/* Namn + badges */}
                      <td>
                        {(u.firstName || u.lastName) ? (
                          <>
                            {u.firstName} {u.lastName}
                            {u.isDev && (
                              <span className="badge bg-secondary ms-2">DEV</span>
                            )}
                            {u.mustCompleteProfile && (
                              <span className="badge bg-warning text-dark ms-2">
                                Ej klar
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-muted">–</span>
                            {u.mustCompleteProfile && (
                              <span className="badge bg-warning text-dark ms-2">
                                Ej klar
                              </span>
                            )}
                          </>
                        )}
                      </td>

                      {/* E-post */}
                      <td>{u.email}</td>

                      {/* Roll */}
                      <td>
                        {u.isDev || u.isProtected ? (
                          <span className="text-muted small">Låst</span>
                        ) : (
                          <select
                            className="form-select form-select-sm"
                            value={u.role}
                            onChange={(e) => changeRole(u, e.target.value)}
                          >
                            <option value="employee">Anställd</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}
                      </td>

                      {/* Skapad */}
                      <td className="text-nowrap">{formatSv(u.createdAt)}</td>

                      {/* Ta bort-knapp */}
                      <td className="text-end">
                        {u.isDev || u.isProtected ? (
                          <span className="text-muted small">Låst</span>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeUser(u)}
                            title="Ta bort användare"
                          >
                            Ta bort
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
